'use client';

import { CheckCircle, FilmStrip, Trash, Warning, X } from '@phosphor-icons/react/ssr';
import { type ElementType, type ReactNode, useId } from 'react';

import { tv } from '../../lib/tv';
import { IconButton } from './icon-button';
import { Link } from './link';
import { ProgressBar } from './progress';

/** Figma "Status": Queued → Uploading → Complete or Error. */
export type UploadStatus = 'queued' | 'uploading' | 'complete' | 'error';

/** "1.2 GB", "350 MB", "12 KB" (decimal units, as the OS shows them). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = -1;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const shown = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${shown} ${units[unit]}`;
}

export type UploadFileItemProps = {
  /** File name - truncated to one line. */
  name: string;
  /** Size in bytes (formatted "1.2 GB") or preformatted text. */
  size: number | string;
  status?: UploadStatus;
  /** 0–100. Drawn while queued, uploading and failed. */
  progress?: number;
  /**
   * Status line. Defaults: "Waiting to upload…", "50%", "Uploaded",
   * "Upload failed". Say the reason on error ("Upload failed — connection lost")
   * and time left while uploading ("50% · about 1 min left").
   */
  statusText?: ReactNode;
  /** File type icon (Figma: film-strip). */
  icon?: ElementType<{ className?: string }>;
  /** Queued, uploading or failed: the × button. */
  onCancel?: () => void;
  /** Complete: the trash button. */
  onRemove?: () => void;
  /** Error: the Retry link. */
  onRetry?: () => void;
  className?: string;
};

const item = tv({
  slots: {
    root: 'bg-bg-surface flex w-full items-center gap-3 rounded-md border border-border-subtle pt-2.5 pr-2 pb-2.5 pl-3',
    fileIcon: 'flex size-9 shrink-0 items-center justify-center rounded-sm',
    fileGlyph: 'size-[18px]',
    body: 'flex min-w-0 flex-1 flex-col gap-1.5',
    top: 'flex items-start gap-2',
    name: 'text-body-sm-strong text-text-primary min-w-0 flex-1 truncate',
    size: 'text-caption text-text-tertiary shrink-0',
    status: 'flex items-center gap-1.5',
    statusIcon: 'size-3.5 shrink-0',
    statusText: 'text-caption text-text-tertiary min-w-0 flex-1',
  },
  variants: {
    status: {
      queued: {
        fileIcon: 'bg-brand-subtle',
        fileGlyph: 'text-icon-brand',
      },
      uploading: {
        fileIcon: 'bg-brand-subtle',
        fileGlyph: 'text-icon-brand',
      },
      complete: {
        fileIcon: 'bg-brand-subtle',
        fileGlyph: 'text-icon-brand',
        statusIcon: 'text-status-success-fg',
        statusText: 'text-status-success-fg',
      },
      error: {
        root: 'border-status-danger-border',
        fileIcon: 'bg-status-danger-bg',
        fileGlyph: 'text-status-danger-fg',
        statusIcon: 'text-status-danger-fg',
        statusText: 'text-status-danger-fg',
      },
    },
  },
  defaultVariants: { status: 'queued' },
});

const DEFAULT_TEXT: Record<UploadStatus, (progress: number) => string> = {
  queued: () => 'Waiting to upload…',
  uploading: (p) => `${p}%`,
  complete: () => 'Uploaded',
  error: () => 'Upload failed',
};

/**
 * One file in an upload list (Figma: File Upload ↳ Upload File Item).
 * Queued → Uploading (bar + % + time left, Cancel) → Complete (check,
 * Remove) or Error (reason, Retry, Cancel). Keep items in upload order;
 * failures stay until retried or removed. The row is a group named by the
 * file; the bar is a Small Progress Bar (Brand, Danger on error); buttons are
 * named with the file.
 */
export function UploadFileItem({
  name,
  size,
  status = 'queued',
  progress = 0,
  statusText,
  icon: Icon = FilmStrip,
  onCancel,
  onRemove,
  onRetry,
  className,
}: UploadFileItemProps) {
  const styles = item({ status });
  const nameId = useId();
  const value = Math.round(Math.min(100, Math.max(0, progress)));
  const StatusIcon =
    status === 'complete' ? CheckCircle : status === 'error' ? Warning : null;

  return (
    <div
      role="group"
      aria-labelledby={nameId}
      data-slot="upload-file-item"
      data-status={status}
      className={styles.root({ className })}
    >
      <span data-slot="upload-file-icon" className={styles.fileIcon()}>
        <Icon aria-hidden className={styles.fileGlyph()} />
      </span>
      <div className={styles.body()}>
        <div className={styles.top()}>
          <span id={nameId} className={styles.name()} title={name}>
            {name}
          </span>
          <span data-slot="upload-file-size" className={styles.size()}>
            {typeof size === 'number' ? formatFileSize(size) : size}
          </span>
        </div>
        {status !== 'complete' && (
          <ProgressBar
            value={value}
            size="sm"
            tone={status === 'error' ? 'danger' : 'brand'}
            aria-label={`Upload progress, ${name}`}
            data-slot="upload-file-progress"
          />
        )}
        <div
          data-slot="upload-file-status"
          className={styles.status()}
          // Status changes are announced; every percent while uploading isn't.
          aria-live={status === 'uploading' ? 'off' : 'polite'}
        >
          {StatusIcon && <StatusIcon aria-hidden className={styles.statusIcon()} />}
          <span className={styles.statusText()}>
            {statusText ?? DEFAULT_TEXT[status](value)}
          </span>
          {status === 'error' && onRetry && (
            // h-5: Figma's 20px Link row (the label stack is 20.25).
            <Link asChild size="sm" className="h-5">
              <button type="button" aria-label={`Retry ${name}`} onClick={onRetry}>
                Retry
              </button>
            </Link>
          )}
        </div>
      </div>
      {status === 'complete'
        ? onRemove && (
            <IconButton
              icon={Trash}
              size="sm"
              aria-label={`Remove ${name}`}
              onClick={onRemove}
            />
          )
        : onCancel && (
            <IconButton
              icon={X}
              size="sm"
              aria-label={`Cancel ${name}`}
              onClick={onCancel}
            />
          )}
    </div>
  );
}
